import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Download, Printer, RotateCcw, RotateCw, Mail, AlertCircle, ArrowLeft, Sun, Moon, MapPin, Percent, Info, BookmarkPlus, Lock } from 'lucide-react';
import Button from '../common/Button';
import EditableField from '../editable/EditableField';
import EditableNumber from '../editable/EditableNumber';
import EditableDate from '../editable/EditableDate';
import { formatCurrency, getCurrencySymbol, getCurrencyOptions } from '../../utils/currencyFormatter';
import { createEmptyLineItem } from '../../utils/calculations';
import { UNDO_HISTORY_SIZE, MAX_CANVAS_WIDTH } from '../../utils/constants';
import { INDIA_STATES, GST_RATES } from '../../utils/indianStates';
import { useTheme } from '../../contexts/ThemeContext';

// ─── Validation Error ────────────────────────────────────────────────────────

const ValidationError = memo(({ message }) => {
  if (!message) return null;
  return (
    <div className="flex items-center gap-1 mt-1 text-red-500 text-xs animate-fade-in">
      <AlertCircle size={11} className="flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
});
ValidationError.displayName = 'ValidationError';

// ─── Currency Dropdown ────────────────────────────────────────────────────────

const CurrencyDropdown = memo(({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border border-gray-300 rounded-lg px-2.5 py-1 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 flex items-center gap-1.5 min-w-[68px]"
      >
        <span>{value}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto min-w-[80px] py-1">
          {options.map(code => (
            <button
              key={code}
              type="button"
              onClick={() => { onChange(code); setOpen(false); }}
              className={`w-full px-3 py-1.5 text-xs text-left hover:bg-blue-50 transition-colors ${value === code ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'}`}
            >
              {code}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
CurrencyDropdown.displayName = 'CurrencyDropdown';

// ─── State Dropdown (Indian States) ─────────────────────────────────────────

const StateDropdown = memo(({ value, onChange, placeholder = 'Select state' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const filtered = useMemo(
    () => INDIA_STATES.filter(s => s.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border border-gray-300 rounded-lg px-2.5 py-1 text-xs bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 min-w-[130px] flex items-center justify-between gap-1.5"
      >
        <span className={value ? 'text-gray-800 font-medium' : 'text-gray-400'}>{value || placeholder}</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform text-gray-400 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-60 py-1">
          <div className="px-2 pt-2 pb-1">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search state..."
              className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
              autoFocus
            />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filtered.length === 0
              ? <div className="px-3 py-2 text-xs text-gray-400">No states found</div>
              : filtered.map(state => (
                  <button
                    key={state}
                    type="button"
                    onClick={() => { onChange(state); setOpen(false); setSearch(''); }}
                    className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${
                      value === state
                        ? 'bg-emerald-50 text-emerald-800 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {state}
                  </button>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
});
StateDropdown.displayName = 'StateDropdown';

// ─── VAT Rate Dropdown ────────────────────────────────────────────────────────

const VATRateDropdown = memo(({ value, rates, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border border-gray-300 rounded-lg px-2.5 py-1 text-xs font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500/30 min-w-[64px] flex items-center justify-between gap-1.5"
      >
        <span className="text-gray-800">{value ?? 20}%</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform text-gray-400 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-[80px] py-1">
          {(rates || [0, 5, 10, 20]).map(rate => (
            <button
              key={rate}
              type="button"
              onClick={() => { onChange(rate); setOpen(false); }}
              className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${
                value === rate ? 'bg-orange-50 text-orange-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {rate}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
VATRateDropdown.displayName = 'VATRateDropdown';

// ─── GST Rate Dropdown ────────────────────────────────────────────────────────

const GSTRateDropdown = memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border border-gray-300 rounded-lg px-2.5 py-1 text-xs font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 min-w-[64px] flex items-center justify-between gap-1.5"
      >
        <span className="text-gray-800">{value ?? 18}%</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform text-gray-400 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-[80px] py-1">
          {GST_RATES.map(rate => (
            <button
              key={rate}
              type="button"
              onClick={() => { onChange(rate); setOpen(false); }}
              className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${
                value === rate ? 'bg-emerald-50 text-emerald-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {rate}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
GSTRateDropdown.displayName = 'GSTRateDropdown';

// ─── Main Component ───────────────────────────────────────────────────────────

function InvoiceCanvas({
  formData,
  updateFormData,
  onDownloadPDF,
  downloadLabel,
  onPrint,
  onEmail,
  onSaveDraft,
  canSaveDraft = false,
  draftSaving = false,
  isDraftMode = false,
  lastSaved,
  isSaving,
  logoPreview,
  onLogoUpload,
  onLogoRemove,
  signaturePreview,
  onSignatureUpload,
  onSignatureRemove,
  validation = {},
  invoiceConfig = null,
  isCalculating = false,
  loading = false,
}) {
  const { isDark, toggle: toggleTheme } = useTheme();
  const [changeHistory, setChangeHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const currencyOptions = useMemo(() => {
    if (invoiceConfig?.currency?.options) return invoiceConfig.currency.options;
    return getCurrencyOptions().map(o => o.code);
  }, [invoiceConfig]);

  const fields = useMemo(() => invoiceConfig?.fields || {}, [invoiceConfig]);
  const labels = useMemo(() => invoiceConfig?.labels || {}, [invoiceConfig]);
  const theme = useMemo(() => invoiceConfig?.theme || {
    primaryColor: '#3b82f6',
    headerBg: '#f0f9ff',
    borderColor: '#e0f2fe',
    invoiceTitle: 'INVOICE',
  }, [invoiceConfig]);

  const { getError = () => null, getLineItemError = () => null, handleBlur = () => {}, handleLineItemBlur = () => {} } = validation;

  const trackChange = useCallback((field, oldVal, newVal) => {
    setChangeHistory(prev => [...prev.slice(-(UNDO_HISTORY_SIZE - 1)), { field, oldVal, newVal }]);
    setRedoStack([]);
  }, []);

  const handleFieldChange = useCallback((field, val) => {
    const old = formData[field];
    updateFormData({ [field]: val });
    trackChange(field, old, val);
  }, [formData, updateFormData, trackChange]);

  const handleFieldBlur = useCallback((field, val) => handleBlur(field, val, formData), [handleBlur, formData]);

  const handleLineItemChange = useCallback((index, field, val) => {
    const newItems = [...formData.items];
    const old = newItems[index][field];
    newItems[index] = { ...newItems[index], [field]: val };
    updateFormData({ items: newItems });
    trackChange(`items[${index}].${field}`, old, val);
  }, [formData.items, updateFormData, trackChange]);

  const handleLineItemFieldBlur = useCallback((index, field, val) => handleLineItemBlur(index, field, val), [handleLineItemBlur]);

  // GST: cascade rate change → all sub-rates
  const handleGSTRateChange = useCallback(totalRate => {
    const rate = parseFloat(totalRate) || 0;
    updateFormData({ tax_rate: rate, cgst_rate: rate / 2, sgst_rate: rate / 2, igst_rate: rate });
  }, [updateFormData]);

  // GST: recompute is_same_state when business state changes
  const handleBusinessStateChange = useCallback(state => {
    const pos = formData.place_of_supply;
    updateFormData({ business_state: state, is_same_state: pos ? (state === pos) : true });
  }, [formData.place_of_supply, updateFormData]);

  // GST: recompute is_same_state when place of supply changes
  const handlePlaceOfSupplyChange = useCallback(pos => {
    const bs = formData.business_state;
    updateFormData({ place_of_supply: pos, is_same_state: bs ? (bs === pos) : true });
  }, [formData.business_state, updateFormData]);

  const addLineItem = useCallback(() => {
    updateFormData({ items: [...formData.items, createEmptyLineItem()] });
  }, [formData.items, updateFormData]);

  const removeLineItem = useCallback(index => {
    if (formData.items.length <= 1) return;
    updateFormData({ items: formData.items.filter((_, i) => i !== index) });
  }, [formData.items, updateFormData]);

  const undo = useCallback(() => {
    if (!changeHistory.length) return;
    const last = changeHistory[changeHistory.length - 1];
    updateFormData({ [last.field.split('[')[0]]: last.oldVal });
    setChangeHistory(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, last]);
  }, [changeHistory, updateFormData]);

  const redo = useCallback(() => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    updateFormData({ [next.field.split('[')[0]]: next.newVal });
    setRedoStack(prev => prev.slice(0, -1));
    setChangeHistory(prev => [...prev, next]);
  }, [redoStack, updateFormData]);

  // Derived state
  const isSameState = formData.is_same_state !== false;
  const isGST = fields.gstin;
  const isVAT = fields.showVATNumber;
  const vatRates = invoiceConfig?.tax_rules?.rates || [0, 5, 10, 20];

  // ─── RENDER ──────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950 overflow-x-hidden">

      {/* Toolbar */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-sm print:hidden">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Link
            to="/"
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="hidden sm:block h-5 border-l border-gray-200 dark:border-gray-700 flex-shrink-0" />
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: theme.primaryColor }}
            />
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[90px] sm:max-w-none">
              {invoiceConfig?.name || 'Invoice Editor'}
            </span>
          </div>
          {isSaving ? (
            <span className="hidden sm:inline text-xs text-blue-600 dark:text-blue-400 font-medium">Saving...</span>
          ) : lastSaved ? (
            <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
              Saved {new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button
            onClick={toggleTheme}
            className="hidden sm:flex p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Toggle theme"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            onClick={undo}
            disabled={!changeHistory.length}
            className="hidden sm:flex p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Undo"
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={redo}
            disabled={!redoStack.length}
            className="hidden sm:flex p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Redo"
          >
            <RotateCw size={16} />
          </button>
          {/* Print — icon only on mobile, icon+text on sm+ */}
          <button
            onClick={onPrint}
            className="p-2 sm:hidden text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Print"
          >
            <Printer size={15} />
          </button>
          <button
            onClick={onPrint}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Printer size={15} />
            Print
          </button>
          {/* Email — icon only on mobile */}
          <button
            onClick={onEmail}
            className="p-2 sm:hidden text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Email invoice to client"
          >
            <Mail size={15} />
          </button>
          <button
            onClick={onEmail}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Email invoice to client"
          >
            <Mail size={15} />
            Email
          </button>
          {/* Save Draft — shown to all logged-in users; free users see locked state */}
          {onSaveDraft && (
            <>
              {/* Mobile: icon only */}
              <button
                onClick={onSaveDraft}
                disabled={draftSaving}
                title={canSaveDraft ? (isDraftMode ? 'Update draft' : 'Save as draft') : 'Save Draft (Pro)'}
                className={`p-2 sm:hidden rounded-lg transition-colors disabled:opacity-50 ${
                  canSaveDraft
                    ? 'text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {canSaveDraft ? <BookmarkPlus size={15} /> : <Lock size={13} />}
              </button>
              {/* Desktop: icon + label */}
              {canSaveDraft ? (
                <button
                  onClick={onSaveDraft}
                  disabled={draftSaving}
                  title={isDraftMode ? 'Update draft' : 'Save as draft'}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-900/40 rounded-lg transition-colors disabled:opacity-50"
                >
                  <BookmarkPlus size={15} />
                  {draftSaving ? 'Saving…' : (isDraftMode ? 'Update Draft' : 'Save Draft')}
                </button>
              ) : (
                <button
                  onClick={onSaveDraft}
                  title="Save Draft (Pro)"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Lock size={13} />
                  Save Draft
                </button>
              )}
            </>
          )}
          <Button
            onClick={onDownloadPDF}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60"
          >
            <Download size={15} />
            <span className="hidden sm:inline">
              {loading ? (downloadLabel ? 'Saving...' : 'Generating...') : (downloadLabel || 'Download PDF')}
            </span>
            <span className="sm:hidden">
              {loading ? '…' : (downloadLabel ? 'Save' : 'PDF')}
            </span>
          </Button>
        </div>
      </header>

      {/* Canvas container */}
      <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 py-6 print:p-0">
        <div className="flex items-start justify-center px-4 print:p-0 print:block">
        <div
          className="bg-white shadow-2xl relative invoice-canvas print:shadow-none p-4 sm:p-[28px] sm:aspect-[210/297]"
          style={{ width: '100%', maxWidth: `${MAX_CANVAS_WIDTH}px` }}
        >
          {/* Colored top stripe */}
          <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t" style={{ backgroundColor: theme.primaryColor }} />

          {/* ── HEADER ── */}
          <div className="flex flex-col sm:flex-row items-start sm:justify-between mt-3 mb-6 pb-5" style={{ borderBottom: `1.5px solid ${theme.borderColor}` }}>

            {/* Left: logo + business info */}
            <div className="flex-1 pr-0 sm:pr-6">
              {/* Logo upload */}
              <div className="mb-3">
                {logoPreview ? (
                  <div className="relative inline-block group">
                    <img
                      src={logoPreview}
                      alt="Business logo"
                      className="h-14 w-auto object-contain cursor-pointer"
                      onClick={() => document.getElementById('logo-input')?.click()}
                    />
                    <button
                      onClick={e => { e.stopPropagation(); onLogoRemove?.(); }}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    >×</button>
                  </div>
                ) : (
                  <button
                    onClick={() => document.getElementById('logo-input')?.click()}
                    className="px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
                  >
                    + Add logo
                  </button>
                )}
                <input id="logo-input" type="file" accept="image/*" onChange={e => { onLogoUpload?.(e); e.target.value = ''; }} className="hidden" />
              </div>

              {/* Business name */}
              <div className="mb-1">
                <EditableField
                  value={formData.business_name}
                  onChange={v => handleFieldChange('business_name', v)}
                  onBlur={v => handleFieldBlur('business_name', v)}
                  placeholder="Your Business Name"
                  className="!border-none !p-0 !bg-transparent text-xl font-bold text-gray-900"
                  error={getError('business_name')}
                />
              </div>

              {/* Business contact */}
              <div className="text-xs text-gray-500 space-y-0.5">
                <EditableField value={formData.business_email} onChange={v => handleFieldChange('business_email', v)} onBlur={v => handleFieldBlur('business_email', v)} placeholder="email@yourbusiness.com" className="!border-none !p-0 !bg-transparent" />
                <EditableField value={formData.business_phone} onChange={v => handleFieldChange('business_phone', v)} placeholder="+91 98765 43210" className="!border-none !p-0 !bg-transparent" />
                <EditableField value={formData.business_address} onChange={v => handleFieldChange('business_address', v)} placeholder="Business address" className="!border-none !p-0 !bg-transparent" />
                {(formData.business_city || formData.business_state || formData.business_zip) && (
                  <div className="flex gap-1 flex-wrap">
                    <EditableField value={formData.business_city} onChange={v => handleFieldChange('business_city', v)} placeholder="City" className="!border-none !p-0 !bg-transparent" />
                  </div>
                )}
              </div>

              {/* VAT: Business VAT Number */}
              {isVAT && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500 font-medium w-14 flex-shrink-0">VAT No.</span>
                    <EditableField
                      value={formData.business_vat_number || ''}
                      onChange={v => handleFieldChange('business_vat_number', v)}
                      placeholder="e.g. DE 123456789"
                      className="!border-none !p-0 !bg-transparent !text-xs font-mono w-full"
                    />
                  </div>
                </div>
              )}

              {/* GST: Business GSTIN + State (shown in seller details) */}
              {isGST && (
                <div className="mt-2.5 pt-2.5 border-t border-gray-100 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500 font-medium w-14 flex-shrink-0">
                      GSTIN <span className="text-red-400">*</span>
                    </span>
                    <div className="flex-1">
                      <EditableField
                        value={formData.business_gstin || ''}
                        onChange={v => handleFieldChange('business_gstin', v)}
                        onBlur={v => handleFieldBlur('business_gstin', v)}
                        placeholder="22AAAAA0000A1Z5"
                        className={`!border-none !p-0 !bg-transparent !text-xs font-mono w-full ${!formData.business_gstin ? 'placeholder-red-300' : ''}`}
                      />
                      {!formData.business_gstin && (
                        <span className="text-[9px] text-red-400">Required for GST invoice</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500 font-medium w-14 flex-shrink-0">
                      Reg. State <span className="text-red-400">*</span>
                    </span>
                    <div>
                      <StateDropdown
                        value={formData.business_state || ''}
                        onChange={handleBusinessStateChange}
                        placeholder="Select seller state"
                      />
                      {!formData.business_state && (
                        <div className="text-[9px] text-red-400 mt-0.5">Required</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right: invoice meta */}
            <div className="text-left sm:text-right sm:flex-shrink-0 mt-4 sm:mt-0">
              <h2 className="text-2xl font-bold mb-4 tracking-wide" style={{ color: theme.primaryColor }}>
                {theme.invoiceTitle || labels.title || 'INVOICE'}
              </h2>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center justify-start sm:justify-end gap-3">
                  <span className="text-gray-500 font-medium">Invoice No.</span>
                  <span className="text-gray-400 italic">
                    {formData.invoice_number || 'Auto-assigned'}
                  </span>
                </div>
                <div className="flex items-center justify-start sm:justify-end gap-3">
                  <span className="text-gray-500 font-medium">Date</span>
                  <EditableDate value={formData.invoice_date} onChange={v => handleFieldChange('invoice_date', v)} className="!border-none !p-0 !bg-transparent" />
                </div>
                <div className="flex items-center justify-start sm:justify-end gap-3">
                  <span className="text-gray-500 font-medium">Due Date</span>
                  <EditableDate value={formData.due_date} onChange={v => handleFieldChange('due_date', v)} className="!border-none !p-0 !bg-transparent" />
                </div>
                {fields.showPONumber && (
                  <div className="flex items-center justify-start sm:justify-end gap-3">
                    <span className="text-gray-500 font-medium">PO Number</span>
                    <EditableField value={formData.po_number || ''} onChange={v => handleFieldChange('po_number', v)} placeholder="PO-001" className="!border-none !p-0 !bg-transparent text-right" />
                  </div>
                )}
                {fields.showCurrency !== false && (
                  <div className="flex items-center justify-start sm:justify-end gap-3 pt-1.5 border-t border-gray-100 mt-1.5">
                    <span className="text-gray-500 font-medium">Currency</span>
                    <CurrencyDropdown
                      value={formData.currency || 'USD'}
                      options={currencyOptions}
                      onChange={code => { handleFieldChange('currency', code); handleFieldChange('currency_symbol', getCurrencySymbol(code)); }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── BILL TO ── */}
          <div className="mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Client */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: theme.primaryColor }}>
                  {labels.clientSection || 'Bill To'}
                </h3>
                <div className="text-xs text-gray-700 space-y-0.5">
                  <EditableField
                    value={formData.client_name}
                    onChange={v => handleFieldChange('client_name', v)}
                    onBlur={v => handleFieldBlur('client_name', v)}
                    placeholder="Client / Company Name"
                    className="!border-none !p-0 !bg-transparent font-semibold text-gray-900"
                    error={getError('client_name')}
                  />
                  <EditableField value={formData.client_address} onChange={v => handleFieldChange('client_address', v)} placeholder="Street address" className="!border-none !p-0 !bg-transparent" />
                  <EditableField value={formData.client_city} onChange={v => handleFieldChange('client_city', v)} placeholder="City, State, ZIP" className="!border-none !p-0 !bg-transparent" />
                  <EditableField value={formData.client_email} onChange={v => handleFieldChange('client_email', v)} onBlur={v => handleFieldBlur('client_email', v)} placeholder="client@email.com" className="!border-none !p-0 !bg-transparent" />
                  <EditableField value={formData.client_phone} onChange={v => handleFieldChange('client_phone', v)} placeholder="Phone number" className="!border-none !p-0 !bg-transparent" />
                  {isGST && (
                    <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100">
                      <span className="text-gray-500 font-medium">GSTIN</span>
                      <EditableField
                        value={formData.client_gstin || ''}
                        onChange={v => handleFieldChange('client_gstin', v)}
                        placeholder="Client GSTIN"
                        className="!border-none !p-0 !bg-transparent font-mono flex-1"
                      />
                    </div>
                  )}
                  {isVAT && (
                    <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100">
                      <span className="text-gray-500 font-medium">VAT No.</span>
                      <EditableField
                        value={formData.client_vat_number || ''}
                        onChange={v => handleFieldChange('client_vat_number', v)}
                        placeholder="Customer VAT No."
                        className="!border-none !p-0 !bg-transparent font-mono flex-1"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* VAT Details Panel */}
              {isVAT && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: theme.primaryColor }}>
                    VAT Details
                  </h3>
                  <div className="rounded-xl border" style={{ borderColor: theme.borderColor }}>
                    {/* Status bar */}
                    <div className="px-3 py-2 flex items-center justify-between rounded-t-xl" style={{ backgroundColor: theme.primaryColor }}>
                      <span className="text-white text-xs font-semibold">
                        {formData.is_reverse_charge ? 'Reverse Charge' : `VAT ${formData.tax_rate ?? 20}%`}
                      </span>
                      <span className="text-white/80 text-xs">
                        {formData.is_reverse_charge ? 'Customer accounts for VAT' : 'Standard rated'}
                      </span>
                    </div>
                    <div className="p-3 space-y-2.5 rounded-b-xl" style={{ backgroundColor: theme.headerBg }}>
                      {/* VAT Rate selector */}
                      {!formData.is_reverse_charge && (
                        <div>
                          <label className="flex items-center gap-1 text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-1">
                            <Percent size={9} />
                            VAT Rate
                          </label>
                          <VATRateDropdown
                            value={formData.tax_rate ?? 20}
                            rates={vatRates}
                            onChange={v => handleFieldChange('tax_rate', v)}
                          />
                        </div>
                      )}
                      {/* Reverse Charge Toggle */}
                      <div>
                        <label className="flex items-center gap-2 text-[10px] text-gray-600 cursor-pointer select-none">
                          <div
                            className="relative w-7 h-4 rounded-full transition-colors cursor-pointer"
                            style={{ backgroundColor: formData.is_reverse_charge ? theme.primaryColor : '#d1d5db' }}
                            onClick={() => handleFieldChange('is_reverse_charge', !formData.is_reverse_charge)}
                          >
                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${formData.is_reverse_charge ? 'left-[14px]' : 'left-0.5'}`} />
                          </div>
                          <span className="font-semibold">Reverse Charge</span>
                        </label>
                        <div className="text-[9px] text-gray-400 mt-0.5">B2B cross-border: supplier charges 0% VAT; buyer self-accounts</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* GST Supply Details */}
              {isGST && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: theme.primaryColor }}>
                    GST Details
                  </h3>
                  <div
                    className="rounded-xl border"
                    style={{ borderColor: theme.borderColor }}
                  >
                    {/* Status bar */}
                    <div
                      className="px-3 py-2 flex items-center justify-between rounded-t-xl"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      <span className="text-white text-xs font-semibold">
                        {isSameState ? 'Intra-State Transaction' : 'Inter-State Transaction'}
                      </span>
                      <span className="text-white/80 text-xs font-medium">
                        {isSameState
                          ? `CGST ${formData.cgst_rate ?? 9}% + SGST ${formData.sgst_rate ?? 9}%`
                          : `IGST ${formData.igst_rate ?? 18}%`}
                      </span>
                    </div>

                    {/* Fields — no overflow-hidden so dropdowns can escape the card */}
                    <div className="p-3 space-y-2.5 rounded-b-xl" style={{ backgroundColor: theme.headerBg }}>
                      {/* Place of Supply */}
                      <div>
                        <label className="flex items-center gap-1 text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-1">
                          <MapPin size={9} />
                          Place of Supply (Buyer State) <span className="text-red-400 ml-0.5">*</span>
                        </label>
                        <StateDropdown
                          value={formData.place_of_supply || ''}
                          onChange={handlePlaceOfSupplyChange}
                          placeholder="Select buyer state"
                        />
                        {!formData.place_of_supply && (
                          <div className="flex items-center gap-1 mt-1 text-[9px] text-amber-600">
                            <Info size={8} className="flex-shrink-0" />
                            <span>Required: determines CGST/SGST vs IGST</span>
                          </div>
                        )}
                      </div>

                      {/* GST Rate */}
                      <div>
                        <label className="flex items-center gap-1 text-gray-500 text-[10px] font-semibold uppercase tracking-wide mb-1">
                          <Percent size={9} />
                          GST Rate
                        </label>
                        <GSTRateDropdown
                          value={formData.tax_rate ?? 18}
                          onChange={handleGSTRateChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── LINE ITEMS TABLE ── */}
          <div className="mb-5">
            {/* Header row */}
            <div
              className="grid px-2 py-2 rounded-t-lg text-xs font-bold"
              style={{
                gap: isMobile ? '4px' : '8px',
                gridTemplateColumns: isMobile ? '1fr 36px 58px 58px 18px' : '1fr 52px 80px 80px 24px',
                backgroundColor: theme.primaryColor,
                color: '#fff',
              }}
            >
              <div>{labels.itemDescription || 'Description'}</div>
              <div className="text-right">{labels.quantity || 'Qty'}</div>
              <div className="text-right">{labels.rate || 'Rate'}</div>
              <div className="text-right">Amount</div>
              <div />
            </div>

            {/* Item rows */}
            <div className="border border-t-0 rounded-b-lg overflow-hidden" style={{ borderColor: theme.borderColor }}>
              {formData.items.map((item, index) => (
                <div
                  key={index}
                  className="grid px-2 py-2 items-center border-t text-xs"
                  style={{
                    gap: isMobile ? '4px' : '8px',
                    gridTemplateColumns: isMobile ? '1fr 36px 58px 58px 18px' : '1fr 52px 80px 80px 24px',
                    borderColor: theme.borderColor,
                    backgroundColor: index % 2 === 0 ? '#fff' : theme.headerBg,
                  }}
                >
                  <div>
                    <EditableField
                      value={item.description}
                      onChange={v => handleLineItemChange(index, 'description', v)}
                      onBlur={v => handleLineItemFieldBlur(index, 'description', v)}
                      placeholder="Service or product description"
                      className="w-full !p-0 !border-none !bg-transparent"
                      error={getLineItemError(index, 'description')}
                    />
                  </div>
                  <div>
                    <EditableNumber
                      value={item.quantity}
                      onChange={v => handleLineItemChange(index, 'quantity', v)}
                      onBlur={v => handleLineItemFieldBlur(index, 'quantity', v)}
                      placeholder="1"
                      min={0}
                      precision={0}
                      className="w-full !p-0 !border-none !bg-transparent text-right"
                    />
                  </div>
                  <div>
                    <EditableNumber
                      value={item.rate}
                      onChange={v => handleLineItemChange(index, 'rate', v)}
                      onBlur={v => handleLineItemFieldBlur(index, 'rate', v)}
                      placeholder="0.00"
                      min={0}
                      className="w-full !p-0 !border-none !bg-transparent text-right"
                    />
                  </div>
                  <div className="text-right font-semibold text-gray-800 currency-display">
                    {formatCurrency(item.amount || 0, formData.currency)}
                  </div>
                  <div className="flex justify-center">
                    {formData.items.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        className="text-gray-300 hover:text-red-500 transition-colors text-lg leading-none"
                        title="Remove item"
                      >×</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addLineItem}
              className="mt-2 text-xs font-medium px-2 py-1 rounded-lg transition-colors"
              style={{ color: theme.primaryColor }}
            >
              + Add line item
            </button>
          </div>

          {/* ── TOTALS ── */}
          <div className="flex justify-end mb-5">
            <div className="w-60">
              {isCalculating && (
                <div className="flex items-center justify-end gap-1.5 text-xs text-blue-500 mb-1.5">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Calculating...</span>
                </div>
              )}

              {/* Subtotal */}
              <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-800 currency-display">{formatCurrency(formData.subtotal || 0, formData.currency)}</span>
              </div>

              {isGST ? (
                /* ── GST totals (Indian standard order) ── */
                <>
                  {/* Discount */}
                  {fields.showDiscount !== false && (
                    <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-red-500 font-medium">Less: Discount</span>
                      <div className="w-24">
                        <EditableNumber
                          value={formData.discount}
                          onChange={v => handleFieldChange('discount', parseFloat(v) || 0)}
                          min={0}
                          className="!p-0 !border-none !bg-transparent text-right text-red-500 font-medium"
                        />
                      </div>
                    </div>
                  )}

                  {/* Taxable value (only when discount > 0) */}
                  {(formData.discount || 0) > 0 && (
                    <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-gray-600">Taxable Value</span>
                      <span className="font-medium text-gray-800 currency-display">
                        {formatCurrency(Math.max(0, (formData.subtotal || 0) - (formData.discount || 0)), formData.currency)}
                      </span>
                    </div>
                  )}

                  {/* CGST + SGST or IGST */}
                  {isSameState ? (
                    <>
                      <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                        <span className="text-gray-600">CGST ({formData.cgst_rate ?? 9}%)</span>
                        <span className="font-medium text-gray-800 currency-display">{formatCurrency(formData.cgst_amount || 0, formData.currency)}</span>
                      </div>
                      <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                        <span className="text-gray-600">SGST ({formData.sgst_rate ?? 9}%)</span>
                        <span className="font-medium text-gray-800 currency-display">{formatCurrency(formData.sgst_amount || 0, formData.currency)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-gray-600">IGST ({formData.igst_rate ?? 18}%)</span>
                      <span className="font-medium text-gray-800 currency-display">{formatCurrency(formData.igst_amount || 0, formData.currency)}</span>
                    </div>
                  )}
                </>
              ) : isVAT ? (
                /* ── VAT totals ── */
                <>
                  {fields.showDiscount !== false && (
                    <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-gray-600">Discount</span>
                      <div className="w-24">
                        <EditableNumber
                          value={formData.discount}
                          onChange={v => handleFieldChange('discount', parseFloat(v) || 0)}
                          min={0}
                          className="!p-0 !border-none !bg-transparent text-right"
                        />
                      </div>
                    </div>
                  )}
                  {formData.is_reverse_charge ? (
                    <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">VAT</span>
                      <span className="text-gray-400 italic text-[10px] self-center">Reverse charge</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                        <span className="text-gray-600">VAT ({formData.tax_rate ?? 20}%)</span>
                        <span className="text-gray-700 currency-display">{formatCurrency(formData.tax_amount || 0, formData.currency)}</span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                /* ── Standard totals ── */
                <>
                  {fields.showTaxRate !== false && (
                    <>
                      <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                        <span className="text-gray-600">{labels.taxLabel || 'Tax'} (%)</span>
                        <div className="w-14">
                          <EditableNumber
                            value={formData.tax_rate}
                            onChange={v => handleFieldChange('tax_rate', parseFloat(v) || 0)}
                            min={0} max={100} step={0.01}
                            className="!p-0 !border-none !bg-transparent text-right"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                        <span className="text-gray-500">Tax amount</span>
                        <span className="text-gray-700 currency-display">{formatCurrency(formData.tax_amount || 0, formData.currency)}</span>
                      </div>
                    </>
                  )}
                  {fields.showDiscount !== false && (
                    <div className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100">
                      <span className="text-gray-600">Discount</span>
                      <div className="w-24">
                        <EditableNumber
                          value={formData.discount}
                          onChange={v => handleFieldChange('discount', parseFloat(v) || 0)}
                          min={0}
                          className="!p-0 !border-none !bg-transparent text-right"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Total */}
              <div className="flex justify-between items-center mt-1.5 px-3 py-2.5 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: theme.primaryColor }}>
                <span>Total Due</span>
                <span className="text-base currency-display">{formatCurrency(formData.total || 0, formData.currency)}</span>
              </div>
            </div>
          </div>

          {/* ── BANK DETAILS ── */}
          {fields.showBankDetails && (
            <div className="mb-4 rounded-xl border overflow-hidden" style={{ borderColor: theme.borderColor }}>
              <div className="px-4 py-2" style={{ backgroundColor: theme.primaryColor }}>
                <h4 className="text-xs font-bold text-white uppercase tracking-wider">Bank / Payment Details</h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs" style={{ backgroundColor: theme.headerBg }}>
                {[
                  ['Bank Name', 'bank_name', 'e.g. HDFC Bank'],
                  ['Account Number', 'bank_account', 'e.g. 1234567890'],
                  ['SWIFT / IFSC', 'bank_swift', 'e.g. HDFC0001234'],
                  ['IBAN', 'bank_iban', 'e.g. IN12 HDFC 0000 1234'],
                ].map(([label, key, ph]) => (
                  <div key={key} className="flex flex-col gap-0.5">
                    <span className="text-gray-400 text-[10px] uppercase tracking-wide">{label}</span>
                    <EditableField
                      value={formData[key] || ''}
                      onChange={v => handleFieldChange(key, v)}
                      placeholder={ph}
                      className="!border-none !p-0 !bg-transparent font-medium text-gray-800"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── NOTES & TERMS ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Notes</h4>
              <textarea
                value={formData.notes || ''}
                onChange={e => handleFieldChange('notes', e.target.value)}
                placeholder={labels.notesPlaceholder || 'Add payment instructions or a thank-you note'}
                className="w-full text-xs p-2.5 border border-gray-200 rounded-lg resize-none h-14 text-gray-700 placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20"
              />
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Terms & Conditions</h4>
              <textarea
                value={formData.terms || ''}
                onChange={e => handleFieldChange('terms', e.target.value)}
                placeholder={labels.termsPlaceholder || 'e.g. Payment due within 30 days of invoice date'}
                className="w-full text-xs p-2.5 border border-gray-200 rounded-lg resize-none h-14 text-gray-700 placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20"
              />
            </div>
          </div>

          {/* ── AUTHORIZED SIGNATORY (GST only) ── */}
          {isGST && (
            <div className="mt-4 flex justify-end">
              <div
                className="w-[38%] border rounded-lg overflow-hidden text-center"
                style={{ borderColor: theme.borderColor }}
              >
                {/* Header */}
                <div className="px-3 py-1.5 text-xs font-semibold" style={{ backgroundColor: theme.headerBg, color: theme.primaryColor }}>
                  For {formData.business_name || 'Your Business'}
                </div>

                {/* Signature image area */}
                <div className="px-3 py-2 flex flex-col items-center gap-1.5" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
                  {signaturePreview ? (
                    <div className="relative group">
                      <img
                        src={signaturePreview}
                        alt="Signature"
                        className="h-10 w-auto object-contain cursor-pointer"
                        onClick={() => document.getElementById('signature-input')?.click()}
                      />
                      <button
                        onClick={e => { e.stopPropagation(); onSignatureRemove?.(); }}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      >×</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => document.getElementById('signature-input')?.click()}
                      className="px-3 py-1.5 border border-dashed rounded text-[10px] transition-colors"
                      style={{ borderColor: theme.borderColor, color: theme.primaryColor }}
                    >
                      + Upload signature
                    </button>
                  )}
                  <input
                    id="signature-input"
                    type="file"
                    accept="image/*"
                    onChange={e => { onSignatureUpload?.(e); e.target.value = ''; }}
                    className="hidden"
                  />
                </div>

                {/* Signatory name */}
                <div className="px-3 py-1.5">
                  <input
                    type="text"
                    value={formData.authorized_signatory || ''}
                    onChange={e => handleFieldChange('authorized_signatory', e.target.value)}
                    placeholder="Signatory name (optional)"
                    className="w-full text-[10px] text-center bg-transparent border-none outline-none text-gray-700 placeholder-gray-300"
                  />
                  <div className="text-[9px] text-gray-400 mt-0.5">Authorized Signatory</div>
                </div>
              </div>
            </div>
          )}

        </div>
        </div>
      </div>

      {/* Bottom download bar */}
      <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-4 sm:px-6 py-4 flex items-center justify-end gap-2 sm:gap-3 print:hidden">
        <button
          onClick={onPrint}
          className="p-2 sm:hidden text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Print"
        >
          <Printer size={15} />
        </button>
        <button
          onClick={onPrint}
          className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <Printer size={15} />
          Print
        </button>
        <button
          onClick={onEmail}
          className="p-2 sm:hidden text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Email invoice to client"
        >
          <Mail size={15} />
        </button>
        <button
          onClick={onEmail}
          className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Email invoice to client"
        >
          <Mail size={15} />
          Email
        </button>
        {/* Save Draft — bottom bar */}
        {onSaveDraft && (
          <>
            {/* Mobile: icon only */}
            <button
              onClick={onSaveDraft}
              disabled={draftSaving}
              title={canSaveDraft ? (isDraftMode ? 'Update draft' : 'Save as draft') : 'Save Draft (Pro)'}
              className={`p-2 sm:hidden rounded-lg transition-colors disabled:opacity-50 ${
                canSaveDraft
                  ? 'text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                  : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {canSaveDraft ? <BookmarkPlus size={15} /> : <Lock size={13} />}
            </button>
            {/* Desktop: icon + label */}
            {canSaveDraft ? (
              <button
                onClick={onSaveDraft}
                disabled={draftSaving}
                title={isDraftMode ? 'Update draft' : 'Save as draft'}
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-900/40 rounded-lg transition-colors disabled:opacity-50"
              >
                <BookmarkPlus size={15} />
                {draftSaving ? 'Saving…' : (isDraftMode ? 'Update Draft' : 'Save Draft')}
              </button>
            ) : (
              <button
                onClick={onSaveDraft}
                title="Save Draft (Pro)"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Lock size={13} />
                Save Draft
              </button>
            )}
          </>
        )}
        <Button
          onClick={onDownloadPDF}
          disabled={loading}
          className="flex items-center gap-1.5 sm:gap-2 text-sm px-4 sm:px-5 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60 font-medium shadow-sm"
        >
          <Download size={15} />
          <span className="hidden sm:inline">
            {loading ? (downloadLabel ? 'Saving...' : 'Generating...') : (downloadLabel || 'Download PDF')}
          </span>
          <span className="sm:hidden">
            {loading ? '…' : (downloadLabel ? 'Save' : 'PDF')}
          </span>
        </Button>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .invoice-canvas {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 12mm !important;
            width: 100% !important;
            max-width: 100% !important;
            aspect-ratio: auto !important;
          }
          input, textarea, select {
            border: none !important;
            background: transparent !important;
            outline: none !important;
          }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default memo(InvoiceCanvas);
